import { CompositeScreenProps, useNavigation } from '@react-navigation/native'
import { externalLinks, links } from 'constants'
import { useTheme } from 'contexts'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { RefreshControl } from 'react-native'
import { ScrollView } from 'react-native-gesture-handler'

import { ChatList } from 'partials/ChatList'
import { MyCoaches } from 'partials/Coaches/MyCoaches'
import { Page } from 'partials/Page'

import { Box } from 'widgets/Box'
import { Button } from 'widgets/Button'
import { Separator } from 'widgets/Separator'
import { Text } from 'widgets/Text'

import { useChatRoomTrainers } from 'hooks/useChatRoomTrainers'
import { useCurrentUser } from 'hooks/useCurrentUser'
import { useDMChatRooms } from 'hooks/useDMChatRooms'
import { useDMRoom } from 'hooks/useDMRoom'
import { useOtherMembers } from 'hooks/useOtherMembers'
import { useQueryClient } from 'hooks/useQueryClient'
import { useUser } from 'hooks/useUser'

import { handleShareAction } from 'utils/sharing'
import commonStyles from 'utils/styles/commonStyles'
import { createPartialUser } from 'utils/user/createPartialUser'

import { useFocusMode } from 'contexts/FocusModeContext'

import { PartialUser } from 'types/user'

type ChatPageProps = {
  withBack?: boolean
}

export const Chat = ({ withBack = true }: ChatPageProps) => {
  const theme = useTheme()
  const queryClient = useQueryClient()
  const { data: currentUser } = useCurrentUser()
  const focusMode = useFocusMode()

  const navigation = useNavigation<any>()

  const {
    sortedChatRooms,
    fetchNextPage,
    isLoading: isLoadingDMChatRooms,
    isFetchingNextPage,
  } = useDMChatRooms()

  const { data: creator } = useUser({ userId: focusMode?.group?.trainer?.id })

  const [roomParticipants, setRoomParticipants] = useState<PartialUser[] | undefined>(undefined)

  const { data: creatorRoom, createRoom } = useDMRoom({ roomParticipants })

  const { data: chatRoomTrainers, isLoading: isLoadingChatRoomTrainers } = useChatRoomTrainers()

  useEffect(() => {
    if (!currentUser || !creator || currentUser.id === creator.id) {
      setRoomParticipants(undefined)
      return
    }
    setRoomParticipants([createPartialUser(currentUser), createPartialUser(creator)])
  }, [currentUser, creator])

  useEffect(() => {
    if (!roomParticipants) return
    createRoom.mutateAsync({
      participants: roomParticipants,
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [roomParticipants])

  const refreshing = useMemo(
    () =>
      !sortedChatRooms ||
      !chatRoomTrainers ||
      (isLoadingDMChatRooms && !isFetchingNextPage) ||
      isLoadingChatRoomTrainers,
    [
      chatRoomTrainers,
      isFetchingNextPage,
      isLoadingChatRoomTrainers,
      isLoadingDMChatRooms,
      sortedChatRooms,
    ],
  )

  const onRefresh = useCallback(() => {
    queryClient.invalidateQueries(['chatRoomTrainers'])
    queryClient.invalidateQueries(['rooms'])
  }, [queryClient])

  const { data: members, isLoading: isLoadingMembers } = useOtherMembers(focusMode?.group)

  const filteredChatRooms = useMemo(() => {
    const returnChatRooms = creatorRoom ? [creatorRoom] : []
    if (members) {
      for (let i = 0; i < sortedChatRooms.length; i += 1) {
        for (let j = 0; j < members.length; j += 1) {
          if (sortedChatRooms[i].reference.ids.includes(members[j].id)) {
            returnChatRooms.push(sortedChatRooms[i])
          }
        }
      }
    }
    return returnChatRooms
  }, [creatorRoom, members, sortedChatRooms])

  const handleInvitePress = async () => {
    const url =
      `${externalLinks.UPSPACE_FULL_PATH}/feed/profile/${currentUser?.username}`.toLocaleLowerCase()

    await handleShareAction(
      url,
      'Join me on Upspace!',
      navigation,
      links.SHARE_PROFILE_PAGE,
      currentUser?.username,
    )
  }

  const isLoading = useMemo(
    () => (!focusMode.group ? isLoadingDMChatRooms || isLoadingChatRoomTrainers : isLoadingMembers),
    [focusMode.group, isLoadingChatRoomTrainers, isLoadingDMChatRooms, isLoadingMembers],
  )

  return (
    <Page
      withNav
      withSearch
      withBack={withBack}
      withExitFocusMode
      isLoading={isLoading}
      navigation={navigation}
      backgroundColor='white'
      enableKeyboardAvoidingView={false}
    >
      <ScrollView
        contentContainerStyle={commonStyles.flexGrow}
        refreshControl={
          <RefreshControl
            refreshing={refreshing}
            onRefresh={onRefresh}
            progressBackgroundColor={theme.colors.v2primary}
            tintColor={theme.colors.v2primary}
          />
        }
      >
        <Box flex={1} margin={{ horizontal: 'md' }}>
          <MyCoaches
            isSquare
            isUpspace
            noCoachesHeaderText='Upspace Groups'
            noCoachesBodyText='Create or join an Upspace to begin a group chat'
            withUpspacesTitle
          />
          <Separator />
          {(focusMode.group ? filteredChatRooms.length > 0 : sortedChatRooms.length > 0) ? (
            <ChatList
              data={focusMode.group ? filteredChatRooms : sortedChatRooms}
              onEndReached={fetchNextPage}
              isSearching={false}
              onRowSelect={() => {}}
            />
          ) : (
            <Box flex={1} margin={{ top: theme.spacing.xl * 2 }}>
              <Text bold align='center' color='greyDarkest'>
                Direct Messages
              </Text>
              <Text align='center' color='greyDark'>
                Start a chat with your friends
              </Text>
              <Box margin={{ top: theme.spacing.xl * 2 }}>
                <Button
                  type='v2primary'
                  borderRadius='sm'
                  padding={{ vertical: 'sm', horizontal: 'md' }}
                  onPress={handleInvitePress}
                >
                  <Text color='white'>Invite A Friend</Text>
                </Button>
              </Box>
            </Box>
          )}
        </Box>
      </ScrollView>
    </Page>
  )
}
